import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Template, TemplateCategory } from '@/api/types'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Spinner } from '@/shared/ui/spinner'
import {
  draftFromTemplate,
  firstInvalidItem,
  hasErrors,
  toUpdateBody,
  validateDraft,
} from '../draft'
import { useDraftEditorLabels } from '../labels'
import { useUpdateTemplate } from '../queries'
import { TemplateDraftEditor } from './template-draft-editor'

/** Mounted only while open, so each opening starts from the saved template. */
export function EditTemplateDialog({
  workspaceId,
  template,
  categories,
  onOpenChange,
}: {
  workspaceId: number
  template: Template
  categories: readonly TemplateCategory[]
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const labels = useDraftEditorLabels()
  const update = useUpdateTemplate()
  const [draft, setDraft] = useState(() => draftFromTemplate(template))
  const [index, setIndex] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const errors = submitted ? validateDraft(draft) : { items: {} }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    const found = validateDraft(draft)
    if (hasErrors(found)) {
      const invalid = firstInvalidItem(found)
      if (invalid !== undefined) setIndex(invalid)
      return
    }
    update.mutate(
      { workspaceId, templateId: template.id, body: toUpdateBody(draft) },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('templates.editTitle')}</DialogTitle>
          <DialogDescription>{t('templates.editDescription')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex min-h-0 flex-col gap-4" noValidate>
          <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
            <TemplateDraftEditor
              draft={draft}
              onChange={setDraft}
              errors={errors}
              categories={categories}
              labels={labels}
              index={index}
              onIndexChange={setIndex}
              rowsEditable={false}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              {t('action.cancel')}
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && <Spinner />}
              {t('action.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
